export const PAYTM_CHECKOUT_FRAME = "paytm_checkout_frame";
export const PAYTM_POLL_INTERVAL_MS = 5000;
export const PAYTM_MAX_POLL_MS = 5 * 60 * 1000;
/** Max status API calls after cancel / payment activity settles. */
export const PAYTM_STATUS_MAX_ATTEMPTS = 3;
export const PAYTM_POST_RETURN_MAX_RETRIES = 8;
export const PAYTM_POST_RETURN_RETRY_DELAY_MS = 2000;
export const PAYTM_PENDING_ORDER_KEY = "paytm_pending_order_id";
export const PAYTM_INCOMPLETE_CANCEL_STREAK = 1;
export const PAYTM_JS_CHECKOUT_ROOT_ID = "paytm-js-checkout-root";

export const buildFrontendPaymentReturnUrl = (orderId = "") => {
  const base = `${window.location.origin}/payment_gateway`;
  if (!orderId) return base;
  return `${base}?order_id=${encodeURIComponent(orderId)}&payment_return=1`;
};

export const wasReferredFromPaytmCallback = () =>
  /\/api\/v1\/paytm\/(?:handbook\/)?callback/i.test(document.referrer || "");

export const extractTxnAmount = (data = {}) =>
  data?.txnAmount ||
  data?.TXN_AMOUNT ||
  data?.txn_amount ||
  data?.amount ||
  data?.final_price ||
  data?.total_price ||
  "";

const isCancelOrCloseNotifyEvent = (eventName = "") => {
  const name = String(eventName || "").toUpperCase();
  return (
    name === "APP_CLOSED" ||
    name === "SESSION_EXPIRED" ||
    name.includes("CANCEL") ||
    name.includes("CLOSED") ||
    name.includes("EXPIRE") ||
    name.includes("LOGIN_FAILURE") ||
    name.includes("BACK") ||
    name.includes("DISMISS")
  );
};

export const loadPaytmCheckoutScript = (mid) =>
  new Promise((resolve, reject) => {
    if (!mid) {
      reject(new Error("Paytm MID missing"));
      return;
    }

    if (window.Paytm?.CheckoutJS) {
      resolve(window.Paytm.CheckoutJS);
      return;
    }

    const existing = document.getElementById("paytm-checkoutjs-script");
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.Paytm?.CheckoutJS) resolve(window.Paytm.CheckoutJS);
        else reject(new Error("Paytm CheckoutJS failed to load"));
      });
      existing.addEventListener("error", () =>
        reject(new Error("Paytm CheckoutJS script error"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = "paytm-checkoutjs-script";
    script.type = "application/javascript";
    script.crossOrigin = "anonymous";
    script.src = `https://securegw.paytm.in/merchantpgpui/checkoutjs/merchants/${encodeURIComponent(
      mid
    )}.js`;

    script.onload = () => {
      const checkout = window.Paytm?.CheckoutJS;
      if (!checkout) {
        reject(new Error("Paytm CheckoutJS unavailable after load"));
        return;
      }
      if (typeof checkout.onLoad === "function") {
        checkout.onLoad(() => resolve(checkout));
      } else {
        resolve(checkout);
      }
    };
    script.onerror = () => reject(new Error("Failed to load Paytm CheckoutJS"));
    document.body.appendChild(script);
  });

/**
 * Open Paytm CheckoutJS with redirect:false so cancel/success stay in-app.
 * onPaymentActivity is called for cancel (APP_CLOSED) and transactionStatus.
 */
export const invokePaytmJsCheckout = async ({
  mid,
  orderId,
  txnToken,
  amount,
  // Empty root = Paytm's default full-page overlay (avoid host-mounted layouts).
  rootId = "",
  onPaymentActivity,
}) => {
  if (!mid || !orderId || !txnToken) {
    console.error("[Paytm][checkout] invoke missing fields", {
      mid: Boolean(mid),
      orderId: Boolean(orderId),
      txnToken: Boolean(txnToken),
    });
    throw new Error("Paytm checkout requires mid, orderId and txnToken");
  }

  console.log("[Paytm][checkout] loading CheckoutJS", { mid, orderId, amount });
  const checkout = await loadPaytmCheckoutScript(mid);
  let activityNotified = false;

  const closeCheckout = () => {
    try {
      if (typeof checkout.close === "function") checkout.close();
    } catch (error) {
      console.warn("[Paytm][checkout] close() failed", error);
    }
  };

  const notifyActivity = (type, payload) => {
    if (activityNotified) {
      console.log("[Paytm][cancel] activity ignored (already handled)", type);
      return;
    }
    activityNotified = true;
    console.log("[Paytm][cancel] payment activity", { type, payload });
    closeCheckout();
    onPaymentActivity?.(type, payload);
  };

  const config = {
    root: rootId ? `#${rootId}` : "",
    flow: "DEFAULT",
    data: {
      orderId: String(orderId),
      token: String(txnToken),
      tokenType: "TXN_TOKEN",
      amount: String(amount || "0"),
    },
    merchant: {
      mid: String(mid),
      redirect: false,
    },
    handler: {
      transactionStatus(data) {
        console.log("[Paytm][checkout] transactionStatus", data);
        notifyActivity("transactionStatus", data);
      },
      notifyMerchant(eventName, data) {
        console.log("[Paytm][checkout] notifyMerchant", eventName, data);
        if (isCancelOrCloseNotifyEvent(eventName)) {
          notifyActivity(String(eventName || "APP_CLOSED"), data);
        }
      },
    },
  };

  console.log("[Paytm][checkout] init + invoke", {
    root: config.root,
    redirect: false,
  });
  await checkout.init(config);
  checkout.invoke();
  return true;
};

export const extractTxnToken = (data) =>
  data?.txnToken ||
  data?.txn_token ||
  data?.transaction_token ||
  data?.transactionToken ||
  "";

export const extractOrderId = (data) =>
  data?.order_id || data?.Order_Id || data?.orderId || "";

export const extractMid = (data) => data?.mid || data?.MID || "";

export const extractPaymentUrl = (data) =>
  data?.payment_url ||
  data?.payment_link ||
  data?.txn_url ||
  data?.paymentUrl ||
  // Do NOT use redirect_url / frontend_return_url — those are SPA return URLs.
  "";

export const normalizePaymentStatus = (data) =>
  (
    data?.paymentStatus ||
    data?.payment_status ||
    data?.data?.paymentStatus ||
    data?.data?.payment_status ||
    ""
  ).toLowerCase();

const getReceipt = (data = {}) =>
  data?.receipt || data?.data?.receipt || {};

/**
 * Collect receipt/gateway failure copy. Backend may send sub_message or sub_heading.
 */
export const getPaymentReceiptFailureText = (data = {}) => {
  const receipt = getReceipt(data);
  return [
    receipt.heading,
    receipt.sub_message,
    receipt.sub_heading,
    receipt.message,
    data?.message,
    data?.statusMessage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/**
 * Receipt already says unsuccessful / cancelled, even if paymentStatus lags as Pending.
 */
export const hasUnsuccessfulReceipt = (data = {}) => {
  const text = getPaymentReceiptFailureText(data);
  if (!text) return false;

  return (
    text.includes("unsuccessful") ||
    text.includes("not successful") ||
    text.includes("payment failed") ||
    text.includes("not completed") ||
    text.includes("incomplete") ||
    text.includes("cancelled") ||
    text.includes("canceled") ||
    text.includes("declined") ||
    text.includes("user has not")
  );
};

export const isTerminalPaymentStatus = (status) =>
  ["completed", "success", "failed", "failure"].includes(status);

export const isPaymentCompleted = (data) => {
  const status = normalizePaymentStatus(data);
  return status === "completed" || status === "success";
};

export const isPaymentFailed = (data) => {
  const status = normalizePaymentStatus(data);
  return (
    status === "failed" ||
    status === "failure" ||
    status === "unsuccessful"
  );
};

export const isPaymentPending = (data) =>
  normalizePaymentStatus(data) === "pending";

export const extractTransactionId = (data = {}) => {
  const receipt = getReceipt(data);
  const orderStatus = data?.orderStatus || data?.data?.orderStatus || {};
  // Only real gateway / receipt transaction ids — never fall back to order_id.
  const candidates = [
    receipt.transaction_id_display,
    receipt.transaction_id,
    orderStatus.TXNID,
    orderStatus.txnId,
    orderStatus.txn_id,
    orderStatus.transaction_id,
    data?.txnId,
    data?.TXNID,
    data?.transaction_id,
    data?.data?.txnId,
  ];

  for (const value of candidates) {
    const text = String(value || "").trim();
    if (text && text !== "--------" && text !== "-") return text;
  }
  return "";
};

const isGenericApiStatusMessage = (message = "") => {
  const normalized = String(message).trim().toLowerCase();
  if (!normalized) return true;

  return (
    normalized.includes("transaction status fetched") ||
    normalized.includes("fetched successfully") ||
    normalized === "success" ||
    normalized === "ok" ||
    normalized === "true"
  );
};

/**
 * Prefer gateway/receipt failure text over API envelope messages
 * (e.g. "Transaction status fetched successfully.").
 */
export const resolvePaymentFailureReason = (
  data = {},
  fallback = "Payment failed"
) => {
  const candidates = [
    data?.receipt?.sub_message,
    data?.receipt?.sub_heading,
    data?.data?.receipt?.sub_message,
    data?.data?.receipt?.sub_heading,
    data?.receipt?.heading,
    data?.data?.receipt?.heading,
    data?.receipt?.message,
    data?.data?.receipt?.message,
    data?.orderStatus?.RESPMSG,
    data?.orderStatus?.respMsg,
    data?.orderStatus?.statusMessage,
    data?.data?.orderStatus?.RESPMSG,
    data?.RESPMSG,
    data?.respMsg,
    data?.statusMessage,
    data?.failure_reason,
    data?.error_message,
    data?.error,
    data?.message,
  ];

  for (const candidate of candidates) {
    const text = String(candidate || "").trim();
    if (!text || isGenericApiStatusMessage(text)) continue;
    return text;
  }

  if (isPaymentFailed(data)) {
    return "Payment failed";
  }

  return fallback;
};

/**
 * User cancelled / abandoned checkout, or bank declined-style "not completed".
 */
export const isPaymentDeclined = (data) => {
  if (isPaymentStillPending(data)) return true;
  return hasUnsuccessfulReceipt(data);
};

export const isTerminalPaymentResult = (data) =>
  isPaymentCompleted(data) ||
  (isPaymentFailed(data) && !isPaymentStillPending(data));

/**
 * Failed + "User has not completed transaction" — temporary while gateway is open
 * or immediately after cancel; do not treat as terminal until Paytm return settles.
 */
export const isPaymentStillPending = (data) => {
  const status = normalizePaymentStatus(data);
  const receiptText = getPaymentReceiptFailureText(data);

  return (
    (status === "failed" || status === "failure") &&
    (receiptText.includes("not completed") ||
      receiptText.includes("incomplete") ||
      receiptText.includes("pending"))
  );
};

/**
 * Unsuccessful UI only when paymentStatus is Failed / Unsuccessful.
 * Pending + receipt "Payment Unsuccessful" must stay Pending Verification.
 */
export const isPaymentUnsuccessfulUi = (data = {}) => isPaymentFailed(data);

export const shouldFinalizePayment = (data) => {
  if (isPaymentCompleted(data)) return true;
  // Incomplete / "not completed" is not a terminal failure.
  if (isPaymentStillPending(data)) return false;
  if (normalizePaymentStatus(data) === "pending") return false;
  return isPaymentFailed(data);
};

// After Paytm redirects back: settle only on Completed or Failed status.
export const shouldFinalizeAfterPaytmReturn = (data) => {
  if (isPaymentCompleted(data)) return true;
  if (normalizePaymentStatus(data) === "pending") return false;
  return isPaymentFailed(data);
};

export const isPaytmCallbackNavigationUrl = (url = "") =>
  /\/api\/v1\/paytm\/(?:handbook\/)?callback/i.test(String(url || ""));

/**
 * Cancel top-level navigations to Paytm callback JSON so the SPA can settle UI.
 * Returns an unsubscribe function. No-op when Navigation API is unavailable.
 */
export const installPaytmCallbackNavigationGuard = (onIntercepted) => {
  const navigationApi = window.navigation;
  if (!navigationApi?.addEventListener) {
    console.log(
      "[Paytm][cancel] Navigation API unavailable — cannot guard top-level callback redirects"
    );
    return () => {};
  }

  console.log("[Paytm][cancel] Navigation API guard installed");

  const onNavigate = (event) => {
    const url = event?.destination?.url || "";
    console.log("[Paytm][cancel] navigate event", {
      url,
      canIntercept: event?.canIntercept,
      cancelable: event?.cancelable,
      navigationType: event?.navigationType,
      isPaytmCallback: isPaytmCallbackNavigationUrl(url),
    });

    if (!isPaytmCallbackNavigationUrl(url)) return;

    console.warn(
      "[Paytm][cancel] Paytm callback top navigation detected — attempting to keep SPA alive"
    );

    // Keep the SPA alive; settle payment status in-app.
    if (typeof event.preventDefault === "function") {
      event.preventDefault();
      console.log("[Paytm][cancel] preventDefault() called");
    }
    if (typeof event.intercept === "function" && event.canIntercept) {
      try {
        event.intercept({
          handler: async () => {
            console.log("[Paytm][cancel] intercept handler → settle status");
            onIntercepted?.(url);
          },
        });
        return;
      } catch (error) {
        console.warn("[Paytm][cancel] intercept failed", error);
      }
    } else if (isPaytmCallbackNavigationUrl(url)) {
      console.warn(
        "[Paytm][cancel] cannot intercept cross-origin callback — calling settle callback anyway"
      );
      onIntercepted?.(url);
    }
  };

  navigationApi.addEventListener("navigate", onNavigate);
  return () => {
    console.log("[Paytm][cancel] Navigation API guard removed");
    navigationApi.removeEventListener("navigate", onNavigate);
  };
};

/**
 * Detect that the checkout iframe left Paytm (callback / app return).
 * Internal Paytm navigations stay cross-origin and must be ignored.
 */
export const hasPaytmCallbackResourceHit = () => {
  try {
    return performance
      .getEntriesByType("resource")
      .some((entry) =>
        /\/api\/v1\/paytm\/(?:handbook\/)?callback/i.test(entry.name || "")
      );
  } catch {
    return false;
  }
};

export const hasPaytmCheckoutReturned = (frame) => {
  if (!frame) return hasPaytmCallbackResourceHit();

  try {
    const href = frame.contentWindow?.location?.href || "";
    if (!href || href === "about:blank") return false;
    // Readable location ⇒ same-origin document (left securegw.paytm.in).
    return true;
  } catch {
    // Cross-origin Paytm page still open, or callback on another host.
  }

  return hasPaytmCallbackResourceHit();
};

/**
 * While gateway is open: never call this.
 * After gateway activity: settle only on Completed / Failed paymentStatus.
 * Pending (even with unsuccessful receipt copy) stays open until poll budget ends.
 */
export const shouldFinalizeFromStatusPoll = (data) => {
  if (isPaymentCompleted(data)) return true;
  if (normalizePaymentStatus(data) === "pending") return false;
  return isPaymentFailed(data);
};

const PAYTM_PAYMENT_MODE_LABELS = {
  CC: "Cards",
  DC: "Cards",
  CREDIT_CARD: "Cards",
  DEBIT_CARD: "Cards",
  CARD: "Cards",
  CARDS: "Cards",
  NB: "Net Banking",
  NET_BANKING: "Net Banking",
  NETBANKING: "Net Banking",
  BANK: "Net Banking",
  UPI: "UPI",
  PPI: "Wallet",
  WALLET: "Wallet",
  PAYTM_WALLET: "Wallet",
  EMI: "EMI",
  BANK_TRANSFER: "Bank Transfer",
  IMPS: "Bank Transfer",
};

/**
 * Map Paytm / backend payment mode codes to human labels (Cards, UPI, etc.).
 */
export const mapPaytmPaymentModeLabel = (raw) => {
  if (raw === null || raw === undefined || raw === "") return undefined;
  const value = String(raw).trim();
  if (!value) return undefined;

  const normalized = value.toUpperCase().replace(/[\s-]+/g, "_");
  if (PAYTM_PAYMENT_MODE_LABELS[normalized]) {
    return PAYTM_PAYMENT_MODE_LABELS[normalized];
  }

  const lower = value.toLowerCase();
  if (lower.includes("upi")) return "UPI";
  if (lower.includes("credit") || lower.includes("debit") || lower.includes("card")) {
    return "Cards";
  }
  if (lower.includes("net") && lower.includes("bank")) return "Net Banking";
  if (lower.includes("wallet")) return "Wallet";
  if (lower.includes("emi")) return "EMI";
  if (lower.includes("bank")) return "Net Banking";

  return value;
};

/**
 * Resolve actual payment instrument from gateway / status API payload.
 */
export const resolveGatewayPaymentMode = (data = {}) => {
  const raw =
    data?.PAYMENTMODE ??
    data?.paymentMode ??
    data?.payment_mode ??
    data?.payment_method ??
    data?.paymentMethod ??
    data?.payment_type ??
    data?.paymentType ??
    data?.gateway_name ??
    data?.gatewayName ??
    data?.receipt?.PAYMENTMODE ??
    data?.receipt?.payment_mode ??
    data?.receipt?.paymentMode ??
    data?.receipt?.payment_method ??
    data?.orderStatus?.PAYMENTMODE ??
    data?.orderStatus?.payment_mode ??
    data?.orderStatus?.paymentMode ??
    data?.data?.PAYMENTMODE ??
    data?.data?.payment_mode ??
    data?.data?.paymentMode ??
    data?.txn?.PAYMENTMODE ??
    data?.txn?.payment_mode;

  return mapPaytmPaymentModeLabel(raw);
};

export const savePendingOrderId = (orderId) => {
  if (orderId) {
    sessionStorage.setItem(PAYTM_PENDING_ORDER_KEY, orderId);
  }
};

export const getPendingOrderId = () =>
  sessionStorage.getItem(PAYTM_PENDING_ORDER_KEY) || "";

export const clearPendingOrderId = () => {
  sessionStorage.removeItem(PAYTM_PENDING_ORDER_KEY);
};

export const clearAwaitPaymentFromUrl = (history) => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("await_payment") !== "1") return;

  params.delete("await_payment");
  const search = params.toString();
  history.replace(`${window.location.pathname}${search ? `?${search}` : ""}`);
};

export const submitPaytmCheckoutInFrame = (paytmData, frameName = PAYTM_CHECKOUT_FRAME) => {
  const paymentUrl = extractPaymentUrl(paytmData);
  const mid = extractMid(paytmData);
  const orderIdValue = extractOrderId(paytmData);
  const txnToken = extractTxnToken(paytmData);

  console.log("[Paytm][checkout] submitPaytmCheckoutInFrame", {
    frameName,
    mid,
    orderId: orderIdValue,
    hasTxnToken: Boolean(txnToken),
    paymentUrl: paymentUrl || null,
  });

  if (paymentUrl) {
    const frame = document.getElementsByName(frameName)[0];
    if (frame) {
      frame.src = paymentUrl;
      console.log("[Paytm][checkout] loaded paymentUrl into iframe");
      return true;
    }
    console.warn("[Paytm][checkout] frame not found for paymentUrl", frameName);
    return false;
  }

  if (!mid || !orderIdValue || !txnToken) {
    console.warn("[Paytm][checkout] missing mid/orderId/txnToken");
    return false;
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.target = frameName;
  form.action = `https://securegw.paytm.in/theia/api/v1/showPaymentPage?mid=${encodeURIComponent(
    mid
  )}&orderId=${encodeURIComponent(orderIdValue)}`;

  const fields = [
    ["body", JSON.stringify({ head: { txnToken } })],
    ["head", JSON.stringify({ txnToken })],
    ["txnToken", txnToken],
    ["mid", mid],
    ["orderId", orderIdValue],
  ];

  fields.forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
  console.log("[Paytm][checkout] posted showPaymentPage form", {
    action: form.action,
  });

  return true;
};
